"use client";

import type { MutationOptions } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import gql from "graphql-tag";
import type {
  CancelAppointmentByCanvasIdInput,
  ScheduledCanvasAppointmentResponse,
} from "@rmh/defs";
import type { GeneratedMutation } from "@aws-amplify/api-graphql";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";

interface CancelAppointmentByCanvasIdVariables {
  input: CancelAppointmentByCanvasIdInput;
}

interface CancelAppointmentByCanvasIdResponse {
  data?: {
    cancelAppointmentByCanvasId: ScheduledCanvasAppointmentResponse;
  };
}

export const cancelAppointmentByCanvasIdMutation = gql`
  mutation cancelAppointmentByCanvasId(
    $input: CancelAppointmentByCanvasIdInput!
  ) {
    cancelAppointmentByCanvasId(input: $input) {
      success
      error {
        type
        message
        details
      }
      data {
        canvasAppointmentId
        canvasPractitionerId
        appointmentDateTime
        timestamp
      }
    }
  }
`.loc?.source.body as GeneratedMutation<
  CancelAppointmentByCanvasIdVariables,
  {
    cancelAppointmentByCanvasId: ScheduledCanvasAppointmentResponse;
  }
>;

export const useCancelAppointmentByCanvasId = (
  options: MutationOptions<
    CancelAppointmentByCanvasIdResponse,
    Error,
    CancelAppointmentByCanvasIdVariables
  > = {}
) => {
  const { mutateAsync, isPending } = useMutation<
    CancelAppointmentByCanvasIdResponse,
    Error,
    CancelAppointmentByCanvasIdVariables
  >({
    ...options,
    mutationFn: async (variables) =>
      fetchGraphQLClient(cancelAppointmentByCanvasIdMutation, variables),
  });

  const cancelAppointmentByCanvasId = async (
    input: CancelAppointmentByCanvasIdInput
  ) => {
    try {
      const response = await mutateAsync({ input });

      return response?.data?.cancelAppointmentByCanvasId;
    } catch (error) {
      console.error("Failed to cancel Canvas appointment by id", error);
      throw error;
    }
  };

  return {
    cancelAppointmentByCanvasId,
    isPending,
  };
};
